/**
 * UnassignedRoleRow Component
 * Placeholder row in the "By Assignee" view for a stakeholder role that has no
 * user assigned yet. Surfaces the unfilled slot and an inline action to assign it.
 */

import { useMemo } from "react";
import { Box, HStack, Icon, VStack } from "@chakra-ui/react";
import { LuUserPlus } from "react-icons/lu";
import CDS from "design-system";
import ProfileAvatar from "components/ProfileAvatar";
import { titleCase } from "helpers/string";
import { ROLE_CONFIG } from "design-system/components/NamePill";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { Task } from "features/Resources/Task/models";
import { isTaskClosed, isTaskOpen } from "features/Resources/Task/helpers";
import EditSupportCrewMemberButton, {
  MANAGER_FIELD_SCHEMA,
  BUDDY_FIELD_SCHEMA,
  PEOPLE_TEAM_CONTACT_FIELD_SCHEMA,
  RECRUITER_FIELD_SCHEMA,
  IT_TEAM_CONTACT_FIELD_SCHEMA,
} from "features/NewHireJourney/Components/EditSupportCrewMemberButton";
import TaskCard from "./TaskCard";
import { StatusBar } from "./UserTaskSummaryRow";

type FieldSchema = typeof BUDDY_FIELD_SCHEMA;

// Roles a user can be assigned to from this view. new_hire always exists and
// mentor is not a journey stakeholder slot, so neither is assignable here.
const ROLE_ASSIGN_CONFIG: Partial<Record<RelationType, { schema: FieldSchema; label: string }>> = {
  manager: { schema: MANAGER_FIELD_SCHEMA, label: "Manager" },
  buddy: { schema: BUDDY_FIELD_SCHEMA, label: "Buddy" },
  people_team_contact: { schema: PEOPLE_TEAM_CONTACT_FIELD_SCHEMA, label: "People Team Contact" },
  recruiter: { schema: RECRUITER_FIELD_SCHEMA, label: "Recruiter" },
  it_team_contact: { schema: IT_TEAM_CONTACT_FIELD_SCHEMA, label: "IT Team Contact" },
};

interface UnassignedRoleRowProps {
  role: RelationType;
  newHireJourneyId: string;
  /** Tasks assigned to this role while it has no user (would otherwise be dropped) */
  tasks?: Task[];
  includeNotStarted?: boolean;
  hideClosed?: boolean;
  onTaskClick?: (task: Task) => void;
}

export default function UnassignedRoleRow({
  role,
  newHireJourneyId,
  tasks = [],
  includeNotStarted = true,
  hideClosed = false,
  onTaskClick,
}: UnassignedRoleRowProps) {
  const assign = ROLE_ASSIGN_CONFIG[role];
  const roleLabel = titleCase(ROLE_CONFIG[role]?.label || role);

  const sortedTasks = useMemo(() => {
    let filtered = tasks;
    if (!includeNotStarted) filtered = filtered.filter((t) => t.status !== "upcoming");
    if (hideClosed) filtered = filtered.filter((t) => !isTaskClosed(t));

    const byDueDate = (a: Task, b: Task) => {
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    };
    return [...filtered].sort(byDueDate);
  }, [tasks, includeNotStarted, hideClosed]);

  const hasTasks = sortedTasks.length > 0;
  const openCount = useMemo(() => sortedTasks.filter(isTaskOpen).length, [sortedTasks]);

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
    >
      <HStack
        width="100%"
        px={4}
        py={2}
        justify="space-between"
        borderBottomWidth={hasTasks ? "1px" : 0}
        borderColor="gray.200"
      >
        {/* Left: muted status bar, placeholder avatar, role label + "Unassigned" */}
        <HStack spacing={3}>
          <StatusBar dotColor="gray" alignSelf="center" />
          <HStack spacing={2} align="center">
            <ProfileAvatar
              user={undefined}
              size="xs"
              bg="gray.100"
              borderColor="gray.200"
              icon={<Icon as={LuUserPlus} color="gray.400" boxSize="3.5" />}
              flexShrink={0}
            />
            <Box textAlign="left">
              <CDS.Text10 fontWeight="600" color="gray.500" lineHeight="1.3">
                {roleLabel}
              </CDS.Text10>
              <CDS.Text12 fontWeight="medium" fontStyle="italic" color="gray.500" lineHeight="1.3">
                Unassigned
              </CDS.Text12>
            </Box>
          </HStack>
        </HStack>

        {/* Right: orphaned-task count + Assign action */}
        <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
          {openCount > 0 && (
            <CDS.Text12 color="gray.600" whiteSpace="nowrap">
              {openCount} Open
            </CDS.Text12>
          )}
          {assign && (
            <EditSupportCrewMemberButton
              newHireJourneyId={newHireJourneyId}
              formSchema={assign.schema}
              label={assign.label}
              renderTrigger={(onOpen) => (
                <CDS.Button
                  size="sm"
                  variant={CDS.Button.variant.outline}
                  intent={CDS.Button.intent.indigo}
                  leftIcon={{ customIcon: <Icon as={LuUserPlus} boxSize="3" /> }}
                  onClick={onOpen}
                >
                  Assign {assign.label}
                </CDS.Button>
              )}
            />
          )}
        </HStack>
      </HStack>

      {hasTasks && (
        <VStack spacing={1} p={3} align="stretch" bg="white">
          {sortedTasks.map((task) => (
            <TaskCard key={task.id} task={task} compact onClick={onTaskClick} />
          ))}
        </VStack>
      )}
    </Box>
  );
}
